import {TextField, Typography} from "@mui/material"
import Grid from "@mui/material/Grid2"

import React, {useState} from "react"
import {useDispatch, useSelector} from "react-redux"
import {ImageButton} from "./ImageButton"
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import {setFirmHeight, setLidCollar, setLidFill, setLidType, unsetFirmHeight} from "../../../features/lid/lidSlice";
import {
    BeveledCollarIcon,
    LiftedCollarIcon,
    PavingReadyCollarIcon,
    WithoutCollarIcon
} from "./CollarIcons";
import a15Image from '../../../image/buttonImage/a15.jpg';
import b125Image from '../../../image/buttonImage/b125.jpg';
import d400Image from '../../../image/buttonImage/d400.jpg';
import e600Image from '../../../image/buttonImage/e600.jpg';
import subFloorImage from '../../../image/buttonImage/subfloor.jpg';
import {AnthraciteIcon, ConcreteGrayIcon} from "./FillIcons";
import {planLimits} from "../../../validation/form/planFormValidation";
import {setInitialFirmHeight} from "../../../features/lid/setInitialFirmHeight";
import {getLidHeightError} from "../../../validation/vault/messages/lidHeight";

const TitleGridItem = () => {
    return (
        <Grid size={12}>
            <Typography component="h1" variant="h4">
                Abdeckung
            </Typography>
            < hr/>
        </Grid>
    )
}

const HeightInput = () => {
    const dispatch = useDispatch();
    const plan = useSelector(state => state.plan);
    const lid = useSelector(state => state.plan.lid);
    const firmHeight = lid ? lid.firmHeight : null;
    const isHeightFirm = lid?.firmHeight !== undefined;
    const [focusHeightField, setFocusHeightField] = useState(false);
    const error = getLidHeightError(plan)

    return (
        <React.Fragment>
            <FormControlLabel
                sx={{my: 2}}
                label="Abdeckungshöhe überschreiben"
                control={
                    <Checkbox
                        checked={isHeightFirm}
                        onChange={(e) => {
                            if (e.target.checked) {
                                dispatch(setInitialFirmHeight())
                                setFocusHeightField(true);
                            } else {
                                dispatch(unsetFirmHeight());
                            }
                        }}
                        name="firmHeight"
                        slotProps={{
                            htmlInput: {
                                'aria-label': 'Abdeckungshöhe überschreiben',
                            },
                        }}
                    />
                }
            />
            <TextField
                disabled={!isHeightFirm}
                inputRef={input => {
                    if (input != null && focusHeightField) input.focus()
                }}
                onBlur={() => setFocusHeightField(false)}
                onFocus={event => event.target.select()}
                type="number"
                slotProps={{
                    htmlInput: {
                        step: planLimits.lidHeightStepWidth,
                        min: 0,
                    }
                }}
                variant="outlined"
                fullWidth
                margin="normal"
                id="height"
                label="Höhe [cm]"
                name="height"
                value={isHeightFirm ? firmHeight : ''}
                error={!!error}
                helperText={error}
                onChange={e => {
                    dispatch(setFirmHeight({height: parseInt(e.target.value)}));
                }}
                style={{width: 207}}
            />
        </React.Fragment>
    )
}

const TypeAndHeightGridItem = () => {
    const dispatch = useDispatch()
    const lid = useSelector(state => state.plan.lid)
    const isAdmin = useSelector(state => !!state.user?.isAdmin)

    return (
        <Grid size={12}>
            <Typography component="p" variant="body1" style={{marginBottom: '0.5em'}}>
                Wählen Sie die Deckelklasse (Druckprüfung nach EN124) aus:
            </Typography>
            <Grid container justifyContent={'flex-start'} alignItems={'flex-end'} spacing={3}>
                <Grid>
                    <ImageButton
                        title={'Klasse A15'}
                        image={a15Image}
                        checked={lid.type === "A15"}
                        onClick={() => {
                            dispatch(setLidType({type: "A15"}))
                        }}
                    />
                </Grid>
                <Grid>
                    <ImageButton
                        title={'Klasse B125'}
                        image={b125Image}
                        checked={lid.type === "B125"}
                        onClick={() => {
                            dispatch(setLidType({type: "B125"}))
                        }}
                    />
                </Grid>
                <Grid>
                    <ImageButton
                        title={'Klasse D400'}
                        image={d400Image}
                        checked={lid.type === "D400"}
                        onClick={() => {
                            dispatch(setLidType({type: "D400"}))
                        }}
                    />
                </Grid>
                <Grid>
                    <ImageButton
                        title={'Klasse E600'}
                        image={e600Image}
                        checked={lid.type === "E600"}
                        onClick={() => {
                            dispatch(setLidType({type: "E600"}))
                        }}
                    />
                </Grid>
                <Grid>
                    <ImageButton
                        title={'Unterflur'}
                        image={subFloorImage}
                        checked={lid.type === "subfloor"}
                        onClick={() => {
                            dispatch(setLidType({type: "subfloor"}))
                        }}
                    />
                </Grid>
                {
                    isAdmin && lid.type !== 'subfloor' && lid.collar !== 'without' &&
                    <Grid size={2}>
                        <HeightInput/>
                    </Grid>
                }
            </Grid>
        </Grid>
    )
}

const CollarGridItem = () => {
    const dispatch = useDispatch()
    const lid = useSelector(state => state.plan.lid)
    const isVisible = () =>
        !!lid && lid.type !== 'subfloor'
    const isWithoutCollarVisible = () =>
        lid.type === 'A15' || lid.type === 'B125' || lid.type === 'D400' || lid.type === 'E600'
    const isBeveledVisible = () =>
        lid.type === 'A15' || lid.type === 'B125' || lid.type === 'D400' || lid.type === 'E600'
    const isLiftedVisible = () =>
        lid.type === 'A15' || lid.type === 'B125' || lid.type === 'D400' || lid.type === 'E600'
    const isPavingReadyVisible = () =>
        lid.type === 'B125' || lid.type === 'D400' || lid.type === 'E600'

    return isVisible()
        ? (
            <Grid size={12}>
                <Typography component="p" variant="body1" style={{marginBottom: '0.5em'}}>
                    Wählen Sie den Betonsockel aus:
                </Typography>
                <Grid container justifyContent={'flex-start'} spacing={3}>
                    {
                        isBeveledVisible() &&
                        <Grid>
                            <ImageButton
                                title={'Abgeschrägt'}
                                checked={lid.collar === "beveled"}
                                onClick={() => {
                                    dispatch(setLidCollar({collar: "beveled"}))
                                }}
                            >
                                <BeveledCollarIcon/>
                            </ImageButton>
                        </Grid>
                    }
                    {
                        isLiftedVisible() &&
                        <Grid>
                            <ImageButton
                                title={'Hochgezogen'}
                                checked={lid.collar === "lifted"}
                                onClick={() => {
                                    dispatch(setLidCollar({collar: "lifted"}))
                                }}
                            >
                                <LiftedCollarIcon/>
                            </ImageButton>
                        </Grid>
                    }
                    {
                        isWithoutCollarVisible() &&
                        <Grid>
                            <ImageButton
                                title={'Ohne'}
                                checked={lid.collar === "without"}
                                onClick={() => {
                                    dispatch(setLidCollar({collar: "without"}))
                                }}
                            >
                                <WithoutCollarIcon/>
                            </ImageButton>
                        </Grid>
                    }
                    {
                        isPavingReadyVisible() &&
                        <Grid>
                            <ImageButton
                                title={'Für Pflästerung'}
                                checked={lid.collar === "pavingready"}
                                onClick={() => {
                                    dispatch(setLidCollar({collar: "pavingready"}))
                                }}
                            >
                                <PavingReadyCollarIcon/>
                            </ImageButton>
                        </Grid>
                    }
                </Grid>
            </Grid>
        )
        : null
}

const FillGridItem = () => {
    const dispatch = useDispatch()
    const lid = useSelector(state => state.plan.lid)
    const isVisible = () => !!lid && lid.type !== 'subfloor'
    const isAnthraciteVisible = () =>
        (lid.type === 'B125' || lid.type === 'D400' || lid.type === 'E600')
        && (lid.collar === 'without' || lid.collar === 'beveled' || lid.collar === 'lifted')
    const isConcreteGrayVisible = () =>
        (lid.type === 'A15' || lid.type === 'B125' || lid.type === 'D400' || lid.type === 'E600')
        && (lid.collar === 'without' || lid.collar === 'beveled' || lid.collar === 'lifted')
    const isPartialFillVisible = () => lid.type === 'B125' && lid.collar === 'pavingready'
    const isNoFillVisible = () => (lid.type === 'D400' || lid.type === 'E600') && lid.collar === 'pavingready'

    return isVisible()
        ? (
            <Grid size={12}>
                <Typography component="p" variant="body1" style={{marginBottom: '0.5em'}}>
                    Wählen Sie die Deckelfarbe/-füllung aus:
                </Typography>
                <Grid container justifyContent={'flex-start'} spacing={3}>
                    {
                        isAnthraciteVisible() &&
                        <Grid>
                            <ImageButton
                                title={'Anthrazit'}
                                checked={lid.fill === "anthracite"}
                                onClick={() => {
                                    dispatch(setLidFill({fill: "anthracite"}))
                                }}
                            >
                                <AnthraciteIcon/>
                            </ImageButton>
                        </Grid>
                    }
                    {
                        isConcreteGrayVisible() &&
                        <Grid>
                            <ImageButton
                                title={'Betongrau'}
                                checked={lid.fill === "concretegray"}
                                onClick={() => {
                                    dispatch(setLidFill({fill: "concretegray"}))
                                }}
                            >
                                <ConcreteGrayIcon/>
                            </ImageButton>
                        </Grid>
                    }
                    {
                        isPartialFillVisible() &&
                        <Grid>
                            <ImageButton
                                title={'Teilfüllung'}
                                checked={lid.fill === "partialfill"}
                            />
                        </Grid>
                    }
                    {
                        isNoFillVisible() &&
                        <Grid>
                            <ImageButton
                                title={'Ohne Betonfüllung'}
                                checked={lid.fill === "without"}
                            />
                        </Grid>
                    }
                </Grid>
            </Grid>
        )
        : null
}

export const LidSection = () => {
    return (
        <Grid container spacing={3}>
            <TitleGridItem/>
            <TypeAndHeightGridItem/>
            <CollarGridItem/>
            <FillGridItem/>
        </Grid>
    )
}
